import { useState, useEffect } from "react";

/* ──────────────── Types ──────────────── */

export interface NearbyCity {
  name: string;
  route: string;
  zips?: string;
}

export interface Benefit {
  icon: string;
  title: string;
  desc: string;
}

export interface Testimonial {
  name: string;
  location: string;
  rating: number;
  text: string;
  service: string;
}

export interface FAQ {
  q: string;
  a: string;
}

export interface ServiceData {
  icon: string;
  title: string;
  description: string;
  bullets: string[];
  badge: string | null;
  link: string;
}

export interface AcceptanceFacility {
  name: string;
  address: string;
  hours: string;
  phone: string;
  notes: string;
}

export interface PhotoLocation {
  name: string;
  address: string;
  hours: string;
  website?: string;
}

export interface DMVLocation {
  name: string;
  address: string;
  hours: string;
  note: string;
  featured?: boolean;
}

export interface Airport {
  code: string;
  name: string;
  distance: string;
  address: string;
  note: string;
  flightUrl: string;
  flightLabel: string;
  featured?: boolean;
}

export interface TravelCard {
  title: string;
  subtitle: string;
  content: string;
  tip: string;
  icon: string;
}

export interface Destination {
  name: string;
  context: string;
  urgency: string;
  icon: string;
}

export interface Stat {
  stat: string;
  label: string;
}

export interface VitalRecordLink {
  icon: string;
  label: string;
  desc: string;
  url: string;
}

export interface CityPageData {
  cityName: string;
  stateName: string;
  stateAbbr: string;
  stateRoute: string;
  heroImageQuery: string;
  heroImageSeq: string;
  heroDescription: string;
  geoPosition: string;
  geoPlacename: string;
  geoRegion: string;
  canonicalUrl: string;
  localAreaTitle: string;
  localAreaText: string[];
  nearbyCities: NearbyCity[];
  benefits: Benefit[];
  serviceAreaTitle: string;
  serviceAreaDescription: string;
  faqs: FAQ[];
  testimonials: Testimonial[];
  nearbyLinks: { name: string; href: string }[];
  introTitle: string;
  introDescription: string;
  localResourcesIntro: string;
  acceptanceFacilities?: AcceptanceFacility[];
  photoLocations?: PhotoLocation[];
  dmvLocations?: DMVLocation[];
  airports?: Airport[];
  travelCards?: TravelCard[];
  destinations?: Destination[];
  stats?: Stat[];
  comparisonIntro?: string;
  mistakesIntro?: string;
  structuredDataServices?: { name: string; description: string }[];
  vitalRecords?: VitalRecordLink[];
}

/* ──────────────── Stars helper ──────────────── */

function Stars({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <i key={i} className="ri-star-fill text-[#F6C344] text-sm"></i>
      ))}
    </div>
  );
}

/* ──────────────── Main Component ──────────────── */

export default function CityPage({ data }: { data: CityPageData }) {
  const {
    cityName,
    stateName,
    stateAbbr,
    stateRoute,
    heroImageQuery,
    heroImageSeq,
    heroDescription,
    geoPosition,
    geoPlacename,
    geoRegion,
    canonicalUrl,
    localAreaTitle,
    localAreaText,
    nearbyCities,
    benefits,
    serviceAreaTitle,
    serviceAreaDescription,
    faqs,
    testimonials,
    nearbyLinks,
    introTitle,
    introDescription,
    localResourcesIntro,
    acceptanceFacilities,
    photoLocations,
    dmvLocations,
    airports,
    travelCards,
    destinations,
    stats,
    comparisonIntro,
    mistakesIntro,
    structuredDataServices = [],
    vitalRecords,
  } = data;

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const [activeNav, setActiveNav] = useState("");

  const navLinks = [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    ...(acceptanceFacilities ? [{ label: "Acceptance Locations", href: "#acceptance-facilities" }] : []),
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    ...(photoLocations ? [{ label: "Local Resources", href: "#local-resources" }] : []),
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
      const offsets = navLinks.map((l) => {
        const el = document.querySelector(l.href);
        if (!el) return { href: l.href, offset: Infinity };
        return { href: l.href, offset: el.getBoundingClientRect().top };
      });
      const visible = offsets.filter((o) => o.offset <= 120);
      if (visible.length) {
        const closest = visible.reduce((a, b) => (a.offset > b.offset ? a : b));
        setActiveNav(closest.href);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [navLinks.length]);

  useEffect(() => {
    document.title = `Emergency Passport ${cityName}, ${stateAbbr} — Expedited Same-Day Service | Fast Passport Center`;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    setLink("canonical", canonicalUrl);
    setMeta(
      "description",
      `Emergency and expedited passport services in ${cityName}, ${stateAbbr} — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving ${stateName} travelers.`
    );
    setMeta("geo.placename", geoPlacename);
    setMeta("geo.region", geoRegion);
    setMeta("geo.position", geoPosition);
    setMeta("ICBM", geoPosition.replace(";", ", "));

    const servicesList =
      structuredDataServices.length > 0
        ? structuredDataServices
        : [
            { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
            { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
            { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
            { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
            { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
            { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
            { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
          ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: stateName, item: `https://fastpassportcenter.com${stateRoute}/` },
          { "@type": "ListItem", position: 3, name: `${cityName} Passport Services`, item: canonicalUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: `Fast Passport Center does not have a physical office in ${cityName} — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for ${cityName} and ${stateName} travelers.`,
        url: canonicalUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: cityName,
          containedInPlace: { "@type": "State", name: stateName },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
        award: "BBB A+ Accredited Business",
        hasCredential: {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "BBB Accreditation",
          recognizedBy: {
            "@type": "Organization",
            name: "Better Business Bureau",
            url: "https://www.bbb.org",
          },
          name: "BBB A+ Rating",
        },
        memberOf: {
          "@type": "Organization",
          name: "Better Business Bureau",
          url: "https://www.bbb.org",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "City", name: cityName },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, [cityName, stateName, stateAbbr, stateRoute, canonicalUrl, geoPlacename, geoRegion, geoPosition, faqs, structuredDataServices]);

  const heroImageUrl = `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28heroImageQuery%29%7D&width=1800&height=700&seq=${heroImageSeq}&orientation=landscape`;

  /* ──────────────── Processing Options ──────────────── */
  const processingOptions = [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ];

  /* ──────────────── Services ──────────────── */
  const services: ServiceData[] = [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: `Urgent passport processing for travelers in ${cityName} with immediate departure needs. Our emergency courier service prioritizes your application and submits directly via reserved agency slots — as fast as 24 hours.`,
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: `Already hold a U.S. passport? We help ${cityName} residents renew fast — no agency visit required for most renewals. Perfect for business travelers, families, and retirees preparing for international travel.`,
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: `First-time applicants typically require an in-person acceptance clerk visit. We guide you through every step, from ${cityName} to the clerk's office, with clear instructions and checklists.`,
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: `Replacements often require additional forms. We guide you through exactly what is needed to avoid costly mistakes and delays, so your business trip or family vacation is not derailed.`,
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: `Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions and checklists so your family vacation stays on schedule.`,
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: `After marriage, divorce, or a legal name change — we assist with corrected passports and data corrections so your travel documents match your reservations.`,
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: `Need a second passport for frequent international travel, or replacing a significantly damaged one? We handle both efficiently — ideal for executives and frequent travelers.`,
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ];

  /* ──────────────── How It Works ──────────────── */
  const steps = [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home or office." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance tailored to your travel situation." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most clients ship from home — no office visit needed." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you do not have to wait at a passport agency." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up — just certainty." },
  ];

  /* ──────────────── Comparison ──────────────── */
  const comparisonRows = [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start — just sign up online or call", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — drive to regional passport agency", fpc: "Most travelers ship documents overnight", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Confirmed emergency travel required — must provide itinerary with confirmation number", fpc: "Most U.S. citizens — emergency travel within one week not required", fpcGood: true },
    { feature: "Cost vs Resellers", agency: "Government fee only — but you pay all travel costs: gas, food, parking, time, possibly hotel and a second trip if told to return next day", fpc: "Best price guarantee — 30–100% less than FedEx, Staples & other non-registered courier resellers", fpcGood: true },
    { feature: "Down to 3 Weeks or Less?", agency: "Regional Passport Agency is your only in-person DIY option — must secure NPIC appointment", fpc: "Fast Passport Center — the only registered courier alternative to an in-person agency visit, as fast as 2–9 business days", fpcGood: true },
  ];

  /* ──────────────── Mistakes ──────────────── */
  const mistakes = [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many travelers assume 6–8 weeks is safe, only to find out it is now 3–4 months. Apply as soon as you confirm travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you are turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you would think." },
  ];

  /* ──────────────── Trust items ──────────────── */
  const trustItems = [
    { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
    { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
    { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
    { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
  ];

  return (
    <div className="font-sans">
      {/* ──────────────── Nav + Hero ──────────────── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[70px]">
          <a href="https://fastpassportcenter.com" className="flex items-center">
            <img src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Header.svg" alt="Fast Passport Center" className={`h-9 w-auto transition-all ${scrolled ? "" : "brightness-0 invert"}`} />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {[{ label: "Services", href: "#services" }, { label: "How It Works", href: "#how-it-works" }, { label: "FAQ", href: "#faq" }].map((item) => (
              <a key={item.href} href={item.href} className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"}`}>{item.label}</a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a href="tel:8772530084" className={`hidden md:inline-flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"}`}>
              <i className="ri-phone-line"></i>877-253-0084
            </a>
            <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] text-sm font-bold px-5 py-2.5 rounded-md transition-all whitespace-nowrap">Get Started</a>
            <button className="md:hidden flex items-center justify-center w-9 h-9" onClick={() => setMobileOpen(!mobileOpen)}>
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl ${scrolled ? "text-[#1A365D]" : "text-white"}`}></i>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
            {[{ label: "Services", href: "#services" }, { label: "How It Works", href: "#how-it-works" }, { label: "FAQ", href: "#faq" }].map((item) => (
              <a key={item.href} href={item.href} onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#1A365D] hover:text-[#F6C344] py-1">{item.label}</a>
            ))}
            <a href="tel:8772530084" className="block text-sm font-semibold text-[#1A365D] py-1"><i className="ri-phone-line mr-1.5"></i>877-253-0084</a>
          </div>
        )}
      </nav>

      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <img src={heroImageUrl} alt={`${cityName} ${stateName} cityscape`} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16 text-center">
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Emergency &amp; Expedited Passport Services in {cityName}, {stateAbbr}
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-3xl mx-auto mb-8 leading-relaxed">
            {heroDescription}
          </p>
          <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
            Start My Passport <i className="ri-arrow-right-line"></i>
          </a>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-5 md:gap-8">
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <div className="flex items-center gap-0.5">
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
              </div>
              <span className="font-semibold">4.9 / 5</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm"><i className="ri-award-line text-[#F6C344] text-base"></i><span>A+ BBB Rating</span></div>
            <div className="flex items-center gap-2 text-white/70 text-sm"><i className="ri-calendar-check-line text-[#F6C344] text-base"></i><span>20+ Years</span></div>
            <div className="flex items-center gap-2 text-white/70 text-sm"><i className="ri-shield-check-line text-[#F6C344] text-base"></i><span>Registered State Dept. Courier</span></div>
            <div className="flex items-center gap-2 text-white/70 text-sm"><i className="ri-price-tag-3-line text-[#F6C344] text-base"></i><span>Best Price Guarantee</span></div>
          </div>
        </div>
      </section>

      {/* ──────────────── Sticky Nav ──────────────── */}
      <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <nav className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-hide">
            {navLinks.map((link) => {
              const isActive = activeNav === link.href;
              return (
                <a key={link.href} href={link.href} className={`whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${isActive ? "bg-[#1A365D] text-white" : "text-gray-500 hover:text-[#1A365D] hover:bg-gray-100"}`}>
                  {link.label}
                </a>
              );
            })}
          </nav>
        </div>
      </div>

      {/* ──────────────── Processing Options ──────────────── */}
      <section id="processing-options" className="bg-white pt-8 pb-20 md:pt-10 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Times</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Processing Options in {cityName}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              {introDescription}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {processingOptions.map((opt, i) => (
              <div key={i} className={`relative rounded-lg p-6 flex flex-col ${opt.highlight ? "bg-[#1A365D] text-white border-2 border-[#F6C344]" : opt.emergency ? "bg-[#F7F8FA] border border-gray-200" : opt.smartSaver ? "bg-[#F7F8FA] border-2 border-green-400" : "bg-[#F7F8FA] border border-gray-200"}`}>
                {opt.highlight && <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#F6C344] text-[#1A365D] text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">Recommended</div>}
                <span className={`self-start text-xs font-semibold px-2.5 py-1 rounded-full mb-4 whitespace-nowrap ${opt.tagColor}`}>{opt.tag}</span>
                <div className="mb-1">
                  <span className={`font-serif text-4xl font-bold ${opt.highlight ? "text-[#F6C344]" : "text-[#1A365D]"}`}>{opt.speed}</span>
                </div>
                <p className={`text-sm font-semibold mb-1 ${opt.highlight ? "text-white/70" : "text-gray-400"}`}>{opt.unit}</p>
                <h3 className={`font-bold text-base mb-4 ${opt.highlight ? "text-white" : "text-[#1A365D]"}`}>{opt.label}</h3>
                {opt.emergency && <p className="text-xs text-gray-400 italic mb-3 leading-relaxed">The closest Regional Passport Agency requires a confirmed NPIC appointment — no walk-ins. Expect a long drive, parking hassle, all-day wait, and you may be told to return the next day to pick up your passport.</p>}
                <ul className="space-y-2 flex-1">
                  {opt.features.map((f, j) => {
                    const isShutdown = f === "Not affected by government shutdowns";
                    const isAgent = f === "Dedicated agent assigned to your case";
                    const isUpdates = f === "Daily status updates — never left guessing";
                    const isNoAgent = f === "No dedicated agent — you are on your own";
                    const isMailUpdates = f === "Updates only by physical mail letter";
                    const isNoContact = f === "No phone or email contact for your case";
                    const isAgentBlock = isAgent || isUpdates;
                    const isNoAgentBlock = isNoAgent || isMailUpdates || isNoContact;
                    const showTopDivider = isShutdown || isAgent || isNoAgent;
                    return (
                      <li key={j} className={`flex items-start gap-2 text-sm ${showTopDivider ? `mt-3 pt-3 border-t ${opt.highlight ? "border-white/20" : "border-gray-200"}` : ""}`}>
                        <i className={`flex-shrink-0 mt-0.5 ${isShutdown ? "ri-shield-check-line text-green-400" : isAgentBlock ? (opt.highlight ? "ri-user-line text-[#F6C344]" : "ri-user-line text-[#1A365D]") : isNoAgentBlock ? "ri-close-line text-red-400" : opt.muted ? "ri-close-line text-red-400" : opt.highlight ? "ri-check-line text-[#F6C344]" : "ri-check-line text-[#F6C344]"}`}></i>
                        <span className={`${isShutdown ? (opt.highlight ? "text-green-300 font-semibold" : "text-green-700 font-semibold") : isAgentBlock ? (opt.highlight ? "text-white font-semibold" : "text-[#1A365D] font-semibold") : isNoAgentBlock ? "text-gray-700 font-semibold" : opt.highlight ? "text-white/80" : opt.muted ? "text-gray-600" : "text-gray-600"}`}>{f}</span>
                      </li>
                    );
                  })}
                </ul>
                {opt.emergency && <div className="mt-6"><a href="#comparison" className="inline-flex items-center justify-center gap-2 border border-gray-300 hover:border-[#1A365D] text-gray-500 hover:text-[#1A365D] font-semibold text-sm px-4 py-2.5 rounded-md transition-all whitespace-nowrap w-full">More Info <i className="ri-information-line"></i></a></div>}
                {opt.highlight && <a href="https://fastpassportcenter.com/checkout/" className="mt-6 inline-flex items-center justify-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-4 py-3 rounded-md transition-all whitespace-nowrap">Start Now <i className="ri-arrow-right-line"></i></a>}
                {opt.smartSaver && <a href="https://fastpassportcenter.com/checkout/" className="mt-6 inline-flex items-center justify-center gap-2 border border-[#1A365D]/20 hover:border-[#1A365D] text-[#1A365D] font-bold text-sm px-4 py-3 rounded-md transition-all whitespace-nowrap">Start Now <i className="ri-arrow-right-line"></i></a>}
                {opt.muted && <a href="#acceptance-facilities" className="mt-6 inline-flex items-center justify-center gap-2 border border-gray-300 hover:border-gray-400 text-gray-400 hover:text-gray-600 font-semibold text-sm px-4 py-2.5 rounded-md transition-all whitespace-nowrap">More Info <i className="ri-information-line"></i></a>}
              </div>
            ))}
          </div>
          <p className="text-center text-gray-400 text-xs mt-6">*Processing speed depends on eligibility and documentation. Not all applicants qualify for Regional Agency Processing.</p>
        </div>
      </section>

      {/* ──────────────── Services ──────────────── */}
      <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services We Expedite for {cityName} Travelers</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              Whether you need a renewal before a business trip, a first-time passport for a family vacation, or an emergency replacement before deployment — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30&ndash;100% less than FedEx, Staples, and other third-party passport courier resellers.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <div key={i} className="group relative bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col">
                {service.badge && <span className="absolute top-4 right-4 bg-[#1A365D] text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">{service.badge}</span>}
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                  <i className={`${service.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm mb-2">{service.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{service.description}</p>
                <ul className="space-y-1.5 mb-6 flex-1">
                  {service.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600"><i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>{b}</li>
                  ))}
                </ul>
                <a href={service.link} className="inline-flex items-center gap-1.5 text-[#1A365D] font-semibold text-sm hover:text-[#F6C344] transition-colors mt-auto whitespace-nowrap">Get Started <i className="ri-arrow-right-line"></i></a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────── Local Area ──────────────── */}
      <section id="service-area" className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">{localAreaTitle}</h2>
          </div>

          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
              {localAreaText.map((p, i) => (
                <p key={i} className="text-gray-600 text-base leading-[1.8] mb-5">{p}</p>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                    <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-base">Fast Passport Center — {cityName}, {stateAbbr}</p>
                    <p className="text-white/60 text-xs mt-0.5">Serving {cityName} &amp; surrounding communities</p>
                  </div>
                </div>
                <p className="text-white/70 text-sm mb-1">
                  <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                  {serviceAreaTitle}
                </p>
                <p className="text-white/50 text-xs mb-5 ml-5">{serviceAreaDescription}</p>
                <div className="border-t border-white/10 pt-5">
                  <p className="text-white/80 text-sm leading-relaxed mb-4">
                    After signing up online, choose what works best for you: <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule courier pickup</strong> from your home or office. Most clients ship overnight — no visit required.
                  </p>
                  <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap">Start My Passport <i className="ri-arrow-right-line"></i></a>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((b, i) => (
                  <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                      <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                    </div>
                    <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
                <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                  <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>Communities We Serve
                </h3>
                <div className="space-y-1.5">
                  {nearbyCities.map((city, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md">
                      <div className="flex items-center gap-2.5">
                        <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                        <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                      </div>
                      {city.zips && <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>}
                    </div>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-4 leading-relaxed">Anywhere in {stateName} — start online, ship overnight, and we handle the rest.</p>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                  <div className="flex flex-wrap gap-2">
                    {nearbyLinks.map((link) => (
                      <a key={link.href} href={link.href} className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap">
                        {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bg-[#1A365D] rounded-lg p-5">
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why {cityName} Travelers Choose Fast Passport Center</p>
                <div className="grid grid-cols-2 gap-3">
                  {trustItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                        <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                      </div>
                      <div className="text-left">
                        <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                        <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ──────────────── Travel Timing (optional) ──────────────── */}
      {travelCards && travelCards.length > 0 && (
        <section className="bg-[#F7F8FA] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for {cityName} Residents</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
                Understanding how {cityName} travelers actually move helps us anticipate the passport urgencies that arise here.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
              {travelCards.map((card, i) => (
                <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                      <i className={`${card.icon} text-[#1A365D] text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A365D] text-sm">{card.title}</h3>
                      <p className="text-gray-400 text-xs">{card.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{card.content}</p>
                  <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
                    <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
                    <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> {card.tip}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ──────────────── Destinations (optional) ──────────────── */}
      {destinations && destinations.length > 0 && stats && (
        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where {cityName} Travelers Go — And Why Passports Get Urgent</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
                These are the destinations and travel patterns we see most from {stateName} clients.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
              {stats.map((s, i) => (
                <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
                  <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
                  <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
              {destinations.map((d, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                      <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
                  <div className="flex items-start gap-2">
                    <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                    <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ──────────────── Testimonials ──────────────── */}
      <section className="bg-[#F7F8FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What {stateName} Travelers Say About Our Emergency &amp; Expedited Passport Service</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Real reviews from {cityName} and {stateName} travelers who trusted Fast Passport Center with their expedited passports.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col">
                <Stars count={t.rating} />
                <p className="text-gray-600 text-sm leading-relaxed mt-4 flex-1 italic">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-5 pt-4 border-t border-gray-200 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-[#1A365D] text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.location}</p>
                  </div>
                  <span className="text-xs font-semibold text-[#F6C344] bg-[#F6C344]/10 px-2.5 py-1 rounded-full whitespace-nowrap">{t.service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────── Acceptance Facilities (optional) ──────────────── */}
      {acceptanceFacilities && acceptanceFacilities.length > 0 && (
        <section id="acceptance-facilities" className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Passport Acceptance Facilities</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where to Submit a New Passport in {cityName}</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance clerk in person to submit their application. {cityName}-area residents can use these facilities — though most travelers using Fast Passport Center ship documents directly to us after their clerk visit.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
              {acceptanceFacilities.map((f, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                      <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A365D] text-sm">{f.name}</h3>
                      <p className="text-gray-500 text-xs mt-0.5">{f.address}</p>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-xs text-gray-500 ml-12">
                    <p><i className="ri-time-line text-[#F6C344] mr-1"></i>{f.hours}</p>
                    <p><i className="ri-phone-line text-[#F6C344] mr-1"></i>{f.phone}</p>
                  </div>
                  <div className="mt-3 ml-12 bg-white rounded-md p-3 border border-gray-200">
                    <p className="text-gray-500 text-xs leading-relaxed">{f.notes}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-[#1A365D] rounded-lg p-6 flex flex-col md:flex-row items-center gap-5 text-center md:text-left">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                <i className="ri-lightbulb-line text-[#1A365D] text-xl"></i>
              </div>
              <div className="flex-1">
                <p className="text-white font-bold text-base mb-1">How This Works With Fast Passport Center</p>
                <p className="text-white/60 text-sm leading-relaxed">Visit the clerk with your sealed envelope, then ship it overnight to Fast Passport Center for expedited processing. We handle the rest — no need to wait for standard mail timelines.</p>
              </div>
              <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">Start Now <i className="ri-arrow-right-line ml-1"></i></a>
            </div>
          </div>
        </section>
      )}

      {/* ──────────────── Appointment Callout ──────────────── */}
      <section className="bg-[#F7F8FA] pt-20 md:pt-28 pb-0">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 md:p-8 flex flex-col md:flex-row items-center gap-5 text-center md:text-left">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
              <i className="ri-calendar-check-line text-[#1A365D] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-serif text-xl md:text-2xl font-bold text-[#1A365D] mb-2">Need the Regional Passport Agency in {cityName}?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">The nearest regional passport agency requires appointments booked weeks in advance — walk-ins are never accepted. Don&apos;t risk showing up and being turned away. Fast Passport Center handles the submission for you with reserved agency access — no appointment needed, no waiting in line.</p>
            </div>
            <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">Get Started <i className="ri-arrow-right-line ml-1"></i></a>
          </div>
        </div>
      </section>

      {/* ──────────────── Comparison ──────────────── */}
      <section id="comparison" className="bg-[#F7F8FA] pt-10 md:pt-14 pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Emergency Passports</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Regional Passport Agency vs. Fast Passport Center</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-base">
              {comparisonIntro || `If you need your passport within two weeks, the regional passport agency is your only do-it-yourself option. Getting an appointment there is rarely straightforward. Fast Passport Center gives you a faster, simpler path without the uncertainty.`}
            </p>
          </div>
          <div className="rounded-lg overflow-hidden border border-gray-200">
            <div className="grid grid-cols-3 bg-[#1A365D] text-white">
              <div className="p-4 text-sm font-semibold text-white/50">Feature</div>
              <div className="p-4 text-sm font-semibold text-center border-l border-white/10"><i className="ri-government-line mr-1.5"></i>Regional Passport Agency</div>
              <div className="p-4 text-sm font-semibold text-center border-l border-white/10 bg-[#F6C344]/15"><i className="ri-shield-check-line mr-1.5 text-[#F6C344]"></i>Fast Passport Center</div>
            </div>
            {comparisonRows.map((row, i) => (
              <div key={i} className={`grid grid-cols-3 border-t border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-[#F7F8FA]"}`}>
                <div className="p-4 text-sm font-semibold text-[#1A365D]">{row.feature}</div>
                <div className="p-4 text-sm text-gray-500 border-l border-gray-100 flex items-start gap-2"><i className="ri-close-circle-line text-red-400 flex-shrink-0 mt-0.5"></i><span>{row.agency}</span></div>
                <div className="p-4 text-sm text-gray-700 border-l border-gray-100 flex items-start gap-2 bg-[#F6C344]/5"><i className="ri-checkbox-circle-line text-[#F6C344] flex-shrink-0 mt-0.5"></i><span className="font-medium">{row.fpc}</span></div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">Start My Expedited Passport <i className="ri-arrow-right-line"></i></a>
          </div>
        </div>
      </section>

      {/* ──────────────── Mistakes ──────────────── */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Emergency &amp; Expedited Passport Application Mistakes in {cityName}</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-base">
              {mistakesIntro || `These mistakes cause the most delays and rejections for ${cityName} travelers. Fast Passport Center reviews your documents before submission to catch these issues — so your trip stays on schedule.`}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
            {mistakes.map((m, i) => (
              <div key={i} className="flex items-start gap-4 bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-all">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className={`${m.icon} text-red-400 text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm mb-1">{m.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{m.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-[#1A365D] rounded-lg p-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
              <i className="ri-shield-check-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-base mb-1">We check everything for you.</p>
              <p className="text-white/70 text-sm mt-1">Fast Passport Center reviews your documents before submission — so you do not have to worry about these mistakes causing a delay before your next international trip.</p>
            </div>
            <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">Get Started</a>
          </div>
        </div>
      </section>

      {/* ──────────────── How It Works ──────────────── */}
      <section id="how-it-works" className="bg-[#1A365D] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Simple Process</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">How Our Expedited Process Works</h2>
            <p className="text-white/60 max-w-xl mx-auto text-base">From start to finish, we make getting an expedited passport in {cityName} as simple as possible — with the efficiency and reliability travelers need. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee.</p>
          </div>
          <div className="relative">
            <div className="hidden lg:block absolute top-10 left-0 right-0 h-px bg-[#F6C344]/20 mx-24"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {steps.map((step, i) => (
                <div key={i} className="relative flex flex-col items-center text-center">
                  <div className="relative z-10 w-20 h-20 flex items-center justify-center rounded-full bg-[#F6C344] mb-5 flex-shrink-0">
                    <div className="flex flex-col items-center">
                      <i className={`${step.icon} text-[#1A365D] text-xl`}></i>
                      <span className="text-[#1A365D] text-xs font-bold leading-none mt-0.5">{step.number}</span>
                    </div>
                  </div>
                  <h3 className="font-bold text-white text-sm mb-2 leading-snug">{step.title}</h3>
                  <p className="text-white/50 text-xs leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-5 md:gap-8">
            <div className="flex items-center gap-2 text-white/70 text-sm"><i className="ri-price-tag-3-line text-[#F6C344] text-base"></i><span>Best Price Guarantee</span></div>
            <div className="flex items-center gap-2 text-white/70 text-sm"><i className="ri-shield-check-line text-[#F6C344] text-base"></i><span>Registered State Dept. Courier</span></div>
            <div className="flex items-center gap-2 text-white/70 text-sm"><i className="ri-award-line text-[#F6C344] text-base"></i><span>A+ BBB Rating</span></div>
          </div>
          <div className="mt-14 text-center">
            <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-10 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">Start My Expedited Passport <i className="ri-arrow-right-line"></i></a>
            <p className="text-white/40 text-xs mt-4">Most {cityName} travelers start online and ship documents overnight — no office visit required for most services.</p>
          </div>
        </div>
      </section>

      {/* ──────────────── FAQ ──────────────── */}
      <section id="faq" className="bg-white py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Questions?</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Frequently Asked <span className="text-[#F6C344]">Questions</span></h2>
            <p className="text-gray-500 text-base">Everything {cityName} and {stateName} travelers ask us most often about expedited passport services.</p>
          </div>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg border border-gray-200 overflow-hidden">
                <button className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-semibold text-[#1A365D] text-sm leading-snug">{faq.q}</span>
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1A365D] flex-shrink-0">
                    <i className={`text-white text-sm transition-transform duration-200 ${openFaq === i ? "ri-subtract-line" : "ri-add-line"}`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-gray-500 text-sm leading-relaxed border-t border-gray-200 pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <p className="text-gray-500 text-sm mb-4">Still have questions? Our team is here to help.</p>
            <a href="tel:8772530084" className="inline-flex items-center gap-2 bg-[#1A365D] hover:bg-[#0F2240] text-white font-semibold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap"><i className="ri-phone-line"></i> Call 877-253-0084</a>
          </div>
        </div>
      </section>

      {/* ──────────────── Helpful Resources (optional) ──────────────── */}
      {(photoLocations || dmvLocations || airports || vitalRecords) && (
        <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for {cityName} Travelers</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">{localResourcesIntro}</p>
            </div>
            <div className="space-y-6">
              {/* Passport Photos */}
              {photoLocations && photoLocations.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                      <i className="ri-camera-line text-white text-lg"></i>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                      <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near {cityName}</p>
                    </div>
                  </div>
                  <div className="px-6 py-6">
                    <p className="text-gray-500 text-sm leading-relaxed mb-4">Your passport application requires two identical 2&quot;&times;2&quot; passport photos taken within the last 6 months. Popular passport photo locations near {cityName} include:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                      {(photoExpanded ? photoLocations : photoLocations.slice(0, 6)).map((loc, i) => (
                        <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                          {loc.website ? (
                            <a href={loc.website} target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] text-xs leading-snug mb-1 block hover:text-[#F6C344] transition-colors">
                              {loc.name} <i className="ri-external-link-line text-[10px] ml-0.5"></i>
                            </a>
                          ) : (
                            <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                          )}
                          {loc.website ? (
                            <a href={loc.website} target="_blank" rel="noopener noreferrer" className="text-gray-500 text-xs leading-relaxed hover:text-[#1A365D] transition-colors block">
                              {loc.address}
                            </a>
                          ) : (
                            <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                          )}
                          <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                        </div>
                      ))}
                    </div>
                    {!photoExpanded && photoLocations.length > 6 && (
                      <button onClick={() => setPhotoExpanded(true)} className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors">
                        <i className="ri-add-circle-line text-sm"></i>Show all {photoLocations.length} photo locations
                      </button>
                    )}
                    <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                      <p className="text-gray-600 text-xs leading-relaxed"><i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i><strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Vital Records */}
              {vitalRecords && vitalRecords.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                      <i className="ri-file-text-line text-white text-lg"></i>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                      <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — {cityName}</p>
                    </div>
                  </div>
                  <div className="px-6 py-6">
                    <p className="text-gray-500 text-sm leading-relaxed mb-5">
                      If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records near {cityName}. Most records can be ordered online for delivery within 5&ndash;10 business days.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                      {vitalRecords.map((record, i) => (
                        <a
                          key={i}
                          href={record.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                        >
                          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                            <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                            <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                            <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                              <i className="ri-external-link-line text-xs"></i>Get Records
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                      <p className="text-gray-600 text-xs leading-relaxed">
                        <i className="ri-information-line text-amber-500 mr-1.5"></i>
                        <strong>Don&apos;t see your county?</strong> If the record you need was issued elsewhere, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* DMV */}
              {dmvLocations && dmvLocations.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                      <i className="ri-id-card-line text-white text-lg"></i>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                      <p className="text-white font-bold text-base leading-tight mt-0.5">DMV Services Near {cityName}</p>
                    </div>
                  </div>
                  <div className="px-6 py-6">
                    <p className="text-gray-500 text-sm leading-relaxed mb-5">A valid government-issued photo ID is required for your passport application. If you need a new or renewed driver&apos;s license or REAL ID, these offices handle DMV services near {cityName}.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                      {dmvLocations.map((loc, i) => (
                        <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                            </div>
                            <div>
                              <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                              <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                              <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                              <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                      <p className="text-gray-600 text-xs leading-relaxed"><i className="ri-calendar-check-line text-amber-500 mr-1.5"></i><strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online before visiting.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Airports */}
              {airports && airports.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                      <i className="ri-flight-takeoff-line text-white text-lg"></i>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                      <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving {cityName} Travelers</p>
                    </div>
                  </div>
                  <div className="px-6 py-6">
                    <p className="text-gray-500 text-sm leading-relaxed mb-5">If your trip is coming up fast, make sure your passport is in hand well before your departure date.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                      {airports.map((airport, i) => (
                        <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                            {airport.featured && <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>}
                          </div>
                          <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                          <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                            <i className="ri-road-map-line"></i><span>{airport.distance}</span>
                          </div>
                          <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                          <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                          <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                            <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                          </a>
                        </div>
                      ))}
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                      <p className="text-gray-600 text-xs leading-relaxed"><i className="ri-passport-line text-amber-500 mr-1.5"></i><strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ──────────────── Final CTA + Footer ──────────────── */}
      <section className="relative bg-[#1A365D] py-20 md:py-28 overflow-hidden">
        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-4">Ready to Get Started?</p>
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">If you need your <span className="text-[#F6C344]">travel documents</span> within hours or a few weeks, don&apos;t wait <span className="text-[#F6C344]">another minute.</span></h2>
          <p className="text-white/70 text-lg mb-8 leading-relaxed">Start your secure order online. We handle the rest — document review, reserved agency submission, and predictable processing. No agency lines. No guessing.</p>
          <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-lg px-10 py-5 rounded-md transition-all hover:scale-105 whitespace-nowrap mb-6">Get Started <i className="ri-arrow-right-line"></i></a>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mb-10">
            <a href="tel:8772530084" className="inline-flex items-center gap-2 text-white/80 hover:text-[#F6C344] font-medium text-base transition-colors whitespace-nowrap"><i className="ri-phone-line text-[#F6C344]"></i> 877-253-0084</a>
            <span className="text-white/30 hidden sm:inline">|</span>
            <a href="mailto:contactus@fastpassportcenter.com" className="inline-flex items-center gap-2 text-white/80 hover:text-[#F6C344] font-medium text-base transition-colors whitespace-nowrap"><i className="ri-mail-line text-[#F6C344]"></i> contactus@fastpassportcenter.com</a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6">
            {[{ icon: "ri-shield-check-line", text: "Registered State Dept. Courier" }, { icon: "ri-award-line", text: "A+ BBB Rating" }, { icon: "ri-lock-line", text: "Secure Checkout" }, { icon: "ri-customer-service-2-line", text: "Real Human Support" }, { icon: "ri-price-tag-3-line", text: "Best Price Guarantee" }].map((badge, i) => (
              <div key={i} className="flex items-center gap-2 text-white/50 text-xs"><i className={`${badge.icon} text-[#F6C344]`}></i><span>{badge.text}</span></div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-[#0F2240] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            <div className="lg:col-span-2">
              <a href="https://fastpassportcenter.com">
                <img src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Footer.svg" alt="Fast Passport Center" className="h-8 w-auto mb-4" />
              </a>
              <p className="text-white/50 text-sm leading-relaxed mb-4 max-w-sm">Fast Passport Center is an approved and registered U.S. Department of State passport courier providing expedited passport services nationwide, including {cityName}, {stateAbbr}.</p>
              <div className="flex items-center gap-3">
                <a href="https://www.facebook.com/fastpassportcenter/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#F6C344] hover:text-[#1A365D] text-white transition-all"><i className="ri-facebook-fill text-sm"></i></a>
                <a href="https://x.com/fpcnews" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#F6C344] hover:text-[#1A365D] text-white transition-all"><i className="ri-twitter-x-line text-sm"></i></a>
                <a href="https://www.instagram.com/fastpassportcenter/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#F6C344] hover:text-[#1A365D] text-white transition-all"><i className="ri-instagram-line text-sm"></i></a>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-sm uppercase tracking-wide text-[#F6C344] mb-4">Services</h4>
              <ul className="space-y-2">
                {[["Passport Renewal", "passport-renewal"], ["New Passport", "new-passport"], ["Lost/Stolen Passport", "lost-stolen-passport"], ["Child Passport", "child-passport"], ["Second Passport", "second-passport"], ["Name Change", "name-change"], ["Damaged Passport", "damaged-passport"], ["Passport Card", "passport-card"]].map(([label, slug]) => (
                  <li key={slug}><a href={`https://fastpassportcenter.com/checkout/?passport=${slug}`} className="text-white/50 hover:text-[#F6C344] text-sm transition-colors whitespace-nowrap">{label}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-sm uppercase tracking-wide text-[#F6C344] mb-4">Contact</h4>
              <ul className="space-y-3">
                <li><a href="tel:8772530084" className="flex items-start gap-2 text-white/50 hover:text-[#F6C344] text-sm transition-colors"><i className="ri-phone-line mt-0.5 flex-shrink-0"></i><span>877-253-0084</span></a></li>
                <li><a href="mailto:contactus@fastpassportcenter.com" className="flex items-start gap-2 text-white/50 hover:text-[#F6C344] text-sm transition-colors"><i className="ri-mail-line mt-0.5 flex-shrink-0"></i><span>contactus@fastpassportcenter.com</span></a></li>
                <li className="flex items-start gap-2 text-white/50 text-sm"><i className="ri-time-line mt-0.5 flex-shrink-0"></i><span>Mon–Fri, 8AM–6PM EST</span></li>
                <li className="flex items-start gap-2 text-white/50 text-sm"><i className="ri-map-pin-line mt-0.5 flex-shrink-0"></i><span>1629 K Street, Suite 300<br />Washington, DC 20006</span></li>
              </ul>
              <div className="mt-6">
                <img src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/01/bbb.png" alt="BBB A+ Rating" className="h-12 w-auto opacity-70" />
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/30 text-xs">&copy; 2026 Fast Passport Center. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="https://fastpassportcenter.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-white/60 text-xs transition-colors whitespace-nowrap">Privacy Policy</a>
              <a href="https://fastpassportcenter.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="text-white/30 hover:text-white/60 text-xs transition-colors whitespace-nowrap">Terms &amp; Conditions</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}